<template>
  <div>
    <button
      :class="{
        'tw-group tw-w-full': true,
        'tw-p-2': allowSelection,
        'hover:tw-opacity-75': allowSelection && !isRenamingUIVisible,
        'tw-bg-deep-orange-200 tw-p-2 hover:tw-bg-deep-orange-200 hover:tw-bg-opacity-70': isSelected
      }"
      :disabled="!allowSelection || isRenamingUIVisible"
      @click="handleSelectFillerSlideAsset"
    >
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
        <div
          :class="{
            'tw-aspect-video tw-h-10 tw-rounded tw-bg-cover tw-bg-center': true,
            'tw-mb-3': isRenamingUIVisible
          }"
          :style="{
            backgroundImage: `url(${fillerSlideAsset.urls.image})`
          }"
        />

        <div v-if="isRenamingUIVisible" class="tw-w-full">
          <form @submit.prevent="handleRenameFillerSlideAsset">
            <StudioInput
              id="filler-slide-asset-new-name-input"
              ref="newNameInput"
              v-model="newName"
              :maxlength="255"
              required
            />
            <div class="tw-mt-2 tw-flex tw-w-full tw-justify-end tw-gap-2">
              <StudioButton
                trackingId="cancel-renaming-filler-slide-item-button"
                size="small"
                variant="outline"
                :disabled="isRenaming"
                @click="hideRenameUI"
              >
                Cancel
              </StudioButton>
              <StudioButton
                trackingId="save-renaming-filler-slide-item-button"
                size="small"
                type="submit"
                :disabled="isRenaming"
              >
                Save
              </StudioButton>
            </div>
          </form>
        </div>
        <div v-else class="tw-flex-1 tw-text-left">
          <div class="tw-break-words tw-break-all tw-text-sm">
            {{ fillerSlideAsset.name }}
          </div>
          <div class="tw-text-xs tw-text-gray-500">
            <template v-if="fillerSlideAsset.status === 'PROCESSING' || fillerSlideAsset.status === 'NOT_GENERATED'">
              Processing...
            </template>
            <template v-else-if="fillerSlideAsset.status === 'GENERATED'"> Processed </template>
            <template v-else> Processing Stopped </template>
          </div>
        </div>

        <StudioButtonGroup
          v-if="!isRenamingUIVisible"
          variant="outline"
          size="small"
          :class="alwaysShowActions ? '' : 'tw-hidden group-hover:tw-flex'"
          :buttons="actionButtons"
        />
      </div>
    </button>
    <StudioConfirmationDialog
      v-if="isConfirmingDelete"
      type="error"
      heading="Are you sure?"
      :actionsDisabled="isDeleting"
      :message="`You are deleting the filler slide <b>${fillerSlideAsset.name}</b>. This action cannot be undone.`"
      :primaryActionTitle="isDeleting ? 'Deleting...' : 'Delete'"
      secondaryActionTitle="Cancel"
      @primaryAction="handleDeleteFillerSlideAsset"
      @secondaryAction="hideDeleteConfirmationDialog"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapActions } from 'vuex';

import { FillerSlideAsset, deleteFillerSlideAsset, renameFillerSlideAsset } from './fillerSlideLibraryService';

import TablerEdit from '@/studioComponents/Icons/tabler/TablerEdit.vue';
import TablerExternalLink from '@/studioComponents/Icons/tabler/TablerExternalLink.vue';
import TablerTrash from '@/studioComponents/Icons/tabler/TablerTrash.vue';
import TablerX from '@/studioComponents/Icons/tabler/TablerX.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioButtonGroup, { StudioButtonGroupButtonProps } from '@/studioComponents/StudioButtonGroup.vue';
import StudioConfirmationDialog from '@/studioComponents/StudioConfirmationDialog.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';

type VisibleActions = {
  rename?: boolean;
  remove?: boolean;
  openPreview?: boolean;
  delete?: boolean;
};

export default defineComponent({
  name: 'FillerSlideLibraryListItem',
  components: {
    StudioInput,
    StudioConfirmationDialog,
    StudioButtonGroup,
    StudioButton
  },
  props: {
    fillerSlideAsset: {
      type: Object as PropType<FillerSlideAsset>,
      required: true
    },
    allowSelection: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    visibleActions: {
      type: Object as PropType<VisibleActions>,
      default: () => ({
        rename: false,
        remove: false,
        openPreview: false,
        delete: false
      })
    },
    isCtaDisabled: {
      type: Boolean,
      default: false
    },
    alwaysShowActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isRenaming: false,
      isRenamingUIVisible: false,
      isConfirmingDelete: false,
      isDeleting: false,
      newName: this.fillerSlideAsset.name
    };
  },
  computed: {
    actionButtons(): StudioButtonGroupButtonProps[] {
      return [
        {
          title: 'Rename Filler Slide',
          hidden: !this.visibleActions.rename,
          clickHandler: this.showRenameUI,
          disabled: this.isRenaming || this.isCtaDisabled,
          iconComponent: TablerEdit,
          trackingId: 'rename-filler-slide-asset-button'
        },
        {
          title: 'Open Preview',
          hidden: !this.visibleActions.openPreview,
          clickHandler: () => {
            window.open(this.fillerSlideAsset.urls.image, '_blank');
          },
          disabled: false,
          iconComponent: TablerExternalLink,
          trackingId: 'open-preview-filler-slide-asset-button'
        },
        {
          title: 'Delete Filler Slide',
          hidden: !this.visibleActions.delete,
          clickHandler: this.showDeleteConfirmationDialog,
          disabled: this.isDeleting,
          iconComponent: TablerTrash,
          trackingId: 'delete-filler-slide-asset-button'
        },
        {
          title: 'Remove',
          hidden: !this.visibleActions.remove,
          clickHandler: this.handleRemoveFillerSlideAsset,
          disabled: this.isCtaDisabled,
          iconComponent: TablerX,
          trackingId: 'remove-filler-slide-asset-button'
        }
      ];
    }
  },
  methods: {
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    showRenameUI(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      this.newName = this.fillerSlideAsset.name;
      this.isRenamingUIVisible = true;
    },
    hideRenameUI(e?: Event): void {
      if (e) {
        e.stopPropagation();
      }

      this.isRenamingUIVisible = false;
    },
    handleRenameFillerSlideAsset(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      const newNameInput: any = this.$refs?.newNameInput;

      if (!newNameInput) {
        return;
      }

      if (!newNameInput.checkValidity()) {
        newNameInput.focus();
        return;
      }

      if (!this.newName.trim()) {
        this.triggerToast({
          mode: 'error',
          msg: 'Video name cannot be empty.'
        });
        return;
      }

      this.isRenaming = false;
      renameFillerSlideAsset(this.fillerSlideAsset.id, this.newName)
        .then((newFillerSlideAsset: FillerSlideAsset) => {
          this.triggerToast({
            mode: 'success',
            msg: 'Filler slide renamed successfully.'
          });
          this.hideRenameUI();
          this.$emit('rename-success', newFillerSlideAsset);
        })
        .catch((errorMessage: string) => {
          this.triggerToast({
            mode: 'error',
            msg: errorMessage
          });
        })
        .finally(() => {
          this.isRenaming = false;
        });
    },
    handleDeleteFillerSlideAsset(): void {
      this.isDeleting = true;
      deleteFillerSlideAsset(this.fillerSlideAsset.id)
        .then(() => {
          this.triggerToast({
            mode: 'success',
            msg: 'Filler slide deleted successfully.'
          });
          this.hideDeleteConfirmationDialog();
          this.$emit('delete-success', this.fillerSlideAsset);
        })
        .catch((errorMessage: string) => {
          this.triggerToast({ mode: 'error', msg: errorMessage });
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
    handleRemoveFillerSlideAsset(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      this.$emit('remove', this.fillerSlideAsset);
    },
    showDeleteConfirmationDialog(e: Event | undefined) {
      if (e) {
        e.stopPropagation();
      }

      this.isConfirmingDelete = true;
    },
    hideDeleteConfirmationDialog() {
      this.isConfirmingDelete = false;
    },
    handleSelectFillerSlideAsset(): void {
      this.$emit('select-filler-slide', this.fillerSlideAsset);
    }
  }
});
</script>
